import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Calculator as CalcIcon, TrendingUp, Wallet as WalletIcon,
  ArrowUpCircle, Target, Info,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const MIN_CAPITAL = 200;
const RATE_MIN = 20;
const RATE_MAX = 44;

const money = (n) =>
  "$" + (Number.isFinite(n) ? n : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Month-by-month compound simulation: profit accrues, then a fixed amount is
// withdrawn (capped at balance), remainder reinvested.
const simulate = (initial, ratePct, withdrawal, months) => {
  const r = ratePct / 100;
  let balance = initial;
  let totalProfit = 0;
  let totalWithdrawn = 0;
  const rows = [];
  for (let m = 1; m <= months; m++) {
    const profit = balance * r;
    balance += profit;
    const w = Math.min(withdrawal, balance);
    balance -= w;
    totalProfit += profit;
    totalWithdrawn += w;
    rows.push({ month: m, profit, withdrawn: w, reinvested: profit - w, balance });
  }
  return { rows, finalBalance: balance, totalProfit, totalWithdrawn };
};

const balanceAtMonth = (initial, ratePct, withdrawal, month) =>
  simulate(initial, ratePct, withdrawal, month).finalBalance;

const StatCard = ({ label, value, icon: Icon, valueClass = "text-white", accent, testid }) => (
  <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#71717A] font-medium">{label}</div>
      {Icon && (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent ? "bg-white/12 text-[#EDEDED]" : "bg-white/5 text-[#71717A]"}`}>
          <Icon size={15} />
        </div>
      )}
    </div>
    <div className={`font-mono text-2xl leading-tight font-semibold mt-3 ${valueClass}`}>{value}</div>
  </div>
);

const Calculator = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(1000);
  const [rate, setRate] = useState(30);
  const [withdrawal, setWithdrawal] = useState(0);
  const [months, setMonths] = useState(12);

  const initial = Math.max(0, Number(amount) || 0);
  const withdraw = Math.max(0, Number(withdrawal) || 0);

  const sim = useMemo(() => simulate(initial, rate, withdraw, months), [initial, rate, withdraw, months]);

  const chartData = useMemo(() => {
    const data = [{ month: 0, balance: initial }];
    sim.rows.forEach((r) => data.push({ month: r.month, balance: r.balance }));
    return data;
  }, [initial, sim]);

  const milestones = [1, 3, 6, 12].map((mo) => ({
    mo,
    balance: balanceAtMonth(initial, rate, withdraw, mo),
  }));

  const roi = initial > 0 ? ((sim.finalBalance + sim.totalWithdrawn - initial) / initial) * 100 : 0;

  return (
    <div className="min-h-screen bybit-grid-subtle">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#000000] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-[#A1A1AA] hover:text-white transition-colors"
                data-testid="calc-back-btn"
              >
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center gap-2">
                <CalcIcon size={22} className="text-[#EDEDED]" />
                <h1 className="font-bold text-xl text-white">Калькулятор доходности</h1>
              </div>
            </div>
            <Link to="/dashboard" className="text-[#EDEDED] hover:text-[#EDEDED] transition-colors font-medium hidden sm:block">
              Дашборд
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ==== Inputs ==== */}
          <div className="lg:col-span-1">
            <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-5 sm:p-6 space-y-6 lg:sticky lg:top-24">
              <div>
                <p className="text-white font-semibold mb-1">Параметры</p>
                <p className="text-[#71717A] text-xs">Смоделируйте рост капитала с реинвестированием.</p>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Сумма инвестиций</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] font-mono">$</span>
                  <input
                    type="number"
                    value={amount}
                    min={MIN_CAPITAL}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg pl-7 pr-3 py-3 text-white font-mono focus:border-white focus:outline-none"
                    data-testid="calc-amount-input"
                  />
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {[200, 500, 1000, 3000, 10000].map((v) => (
                    <button
                      key={v}
                      onClick={() => setAmount(v)}
                      className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-[#A1A1AA] hover:bg-[#1A1A1A]/15 hover:text-[#EDEDED] transition-colors font-mono"
                      data-testid={`calc-preset-${v}`}
                    >
                      ${v.toLocaleString()}
                    </button>
                  ))}
                </div>
                <p className="text-[#52525B] text-[11px] mt-2">Рекомендуемый минимум ${MIN_CAPITAL}</p>
              </div>

              {/* Rate slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[#A1A1AA] text-sm">Доходность в месяц</label>
                  <span className="font-mono text-[#EDEDED] font-semibold" data-testid="calc-rate-value">{rate}%</span>
                </div>
                <input
                  type="range"
                  min={RATE_MIN}
                  max={RATE_MAX}
                  step={1}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full accent-[#FF8A1E] cursor-pointer"
                  data-testid="calc-rate-slider"
                />
                <div className="flex justify-between text-[10px] text-[#52525B] font-mono mt-1">
                  <span>{RATE_MIN}%</span>
                  <span>{RATE_MAX}%</span>
                </div>
              </div>

              {/* Monthly withdrawal */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Вывод в месяц</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] font-mono">$</span>
                  <input
                    type="number"
                    value={withdrawal}
                    min={0}
                    onChange={(e) => setWithdrawal(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg pl-7 pr-3 py-3 text-white font-mono focus:border-white focus:outline-none"
                    data-testid="calc-withdrawal-input"
                  />
                </div>
                <p className="text-[#52525B] text-[11px] mt-2">0 = всё реинвестируется</p>
              </div>

              {/* Months slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[#A1A1AA] text-sm">Период</label>
                  <span className="font-mono text-white font-semibold" data-testid="calc-months-value">{months} мес.</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={24}
                  step={1}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="w-full accent-[#FF8A1E] cursor-pointer"
                  data-testid="calc-months-slider"
                />
                <div className="flex justify-between text-[10px] text-[#52525B] font-mono mt-1">
                  <span>1</span><span>12</span><span>24</span>
                </div>
              </div>
            </div>
          </div>

          {/* ==== Results ==== */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard label={`Баланс через ${months} мес.`} value={money(sim.finalBalance)} icon={WalletIcon} accent testid="calc-final-balance" />
              <StatCard label="Всего прибыли" value={money(sim.totalProfit)} icon={TrendingUp} valueClass="text-[#34D399] pnl-glow-up" testid="calc-total-profit" />
              <StatCard label="Всего выведено" value={money(sim.totalWithdrawn)} icon={ArrowUpCircle} testid="calc-total-withdrawn" />
              <StatCard label="ROI (общий)" value={`+${roi.toFixed(0)}%`} icon={Target} valueClass="text-[#34D399] pnl-glow-up" testid="calc-roi" />
            </div>

            {/* Milestones */}
            <div>
              <p className="text-[#71717A] text-[11px] uppercase tracking-[0.12em] font-medium mb-3">Прогноз баланса</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {milestones.map((m) => (
                  <div key={m.mo} className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-4" data-testid={`calc-milestone-${m.mo}`}>
                    <div className="text-[#A1A1AA] text-xs">{m.mo} мес.</div>
                    <div className="font-mono text-lg font-semibold text-white mt-1">{money(m.balance)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-4 sm:p-5">
              <p className="text-white font-semibold mb-4">Рост капитала</p>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="balFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#EDEDED" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#EDEDED" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                    <XAxis dataKey="month" stroke="#52525B" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}м`} />
                    <YAxis stroke="#52525B" tick={{ fontSize: 11 }} width={54} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                    <Tooltip
                      contentStyle={{ background: "#1F1F1F", border: "1px solid #3F3F46", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#A1A1AA" }}
                      formatter={(val) => [money(val), "Баланс"]}
                      labelFormatter={(l) => `Месяц ${l}`}
                    />
                    <Area type="monotone" dataKey="balance" name="Баланс" stroke="#EDEDED" strokeWidth={2.5} fill="url(#balFill)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Monthly breakdown table */}
            <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
              <div className="p-4 border-b border-[#1F1F1F]">
                <p className="text-white font-semibold">Помесячная разбивка</p>
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full" data-testid="calc-table">
                  <thead>
                    <tr className="border-b border-[#1F1F1F]">
                      {["Месяц", "Прибыль", "Выведено", "Реинвест.", "Итог"].map((h) => (
                        <th key={h} className="text-left text-[#71717A] text-[11px] uppercase tracking-wider font-medium py-3 px-4">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sim.rows.map((r) => (
                      <tr key={r.month} className="border-b border-[#1F1F1F]/50 hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 px-4 text-white font-mono text-sm">{r.month}</td>
                        <td className="py-3 px-4 font-mono text-sm text-[#34D399]">+{money(r.profit)}</td>
                        <td className="py-3 px-4 font-mono text-sm text-[#A1A1AA]">{r.withdrawn > 0 ? "-" + money(r.withdrawn) : money(0)}</td>
                        <td className="py-3 px-4 font-mono text-sm text-white">{money(r.reinvested)}</td>
                        <td className="py-3 px-4 font-mono text-sm text-white font-semibold">{money(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-[#1F1F1F]">
                {sim.rows.map((r) => (
                  <div key={r.month} className="p-4" data-testid={`calc-row-${r.month}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#A1A1AA] text-xs">Месяц {r.month}</span>
                      <span className="font-mono text-white font-semibold">{money(r.balance)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="text-[#34D399]">+{money(r.profit)}</span>
                      <span className="text-[#A1A1AA]">{r.withdrawn > 0 ? "вывод -" + money(r.withdrawn) : "реинвест"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 text-[#71717A] text-xs bg-[#0A0A0A] border border-[#1F1F1F] rounded-xl p-4">
              <Info size={16} className="text-[#EDEDED] flex-shrink-0 mt-0.5" />
              <span>
                Расчёт носит иллюстративный характер и основан на средних исторических показателях копитрейдинга.
                Это не гарантия будущей доходности — реальные результаты могут отличаться.
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Calculator;
